import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { supabase } from '@/integrations/supabase/client';
import { 
  ArrowLeft, 
  PlayCircle, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Calendar,
  Loader2,
  Settings,
  RefreshCw,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

interface CronExecution {
  id: string;
  status: string;
  created_at: string;
  execution_time_seconds: number;
  articles_published: number;
  error_message: string | null;
  execution_log: any;
}

export default function SchedulerDashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isRunning, setIsRunning] = useState(false);

  // Fetch last execution
  const { data: lastExecution, isLoading: loadingExecution } = useQuery({
    queryKey: ['last-cron-execution'],
    queryFn: async () => {
      const { data } = await supabase
        .from('cron_executions')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      return data as CronExecution | null;
    },
    refetchInterval: 30000
  });

  // Fetch execution history
  const { data: executionHistory, isLoading: loadingHistory } = useQuery({
    queryKey: ['cron-executions-history'],
    queryFn: async () => {
      const { data } = await supabase
        .from('cron_executions')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);
      return data as CronExecution[];
    }
  });

  // Check cron status
  const { data: cronStatus, refetch: refetchStatus } = useQuery({
    queryKey: ['cron-status'],
    queryFn: async () => {
      try {
        const { data } = await supabase.functions.invoke('check-cron-status');
        return data;
      } catch {
        return { isSetup: false, isActive: false };
      }
    }
  });

  // Manual run mutation
  const runNow = useMutation({
    mutationFn: async () => {
      setIsRunning(true);
      toast.info('Starting automation pipeline...', {
        description: 'This will take 3-5 minutes'
      });

      const { data, error } = await supabase.functions.invoke('daily-automation', {
        body: { manual_trigger: true }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      toast.success('Automation completed successfully!', {
        description: `Published ${data?.articlesPublished || 0} articles`
      });
      queryClient.invalidateQueries({ queryKey: ['last-cron-execution'] });
      queryClient.invalidateQueries({ queryKey: ['cron-executions-history'] });
    },
    onError: (error: any) => {
      toast.error('Automation failed', {
        description: error.message
      });
    },
    onSettled: () => {
      setIsRunning(false);
    }
  });

  const getNextRunTime = () => {
    const now = new Date();
    const nextRun = new Date();
    nextRun.setUTCHours(8, 0, 0, 0);
    
    if (now.getUTCHours() >= 8) {
      nextRun.setDate(nextRun.getDate() + 1);
    }
    
    const hoursUntil = Math.floor((nextRun.getTime() - now.getTime()) / (1000 * 60 * 60));
    const minutesUntil = Math.floor(((nextRun.getTime() - now.getTime()) % (1000 * 60 * 60)) / (1000 * 60));
    
    return { nextRun, hoursUntil, minutesUntil };
  };

  const { nextRun, hoursUntil, minutesUntil } = getNextRunTime();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  return (
    <div className="container mx-auto p-8 max-w-7xl">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-4 mb-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-4xl font-bold">Automation Scheduler</h1>
          </div>
          <p className="text-muted-foreground">
            Schedule and manage daily crypto content automation
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetchStatus()}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Status Cards */}
      <div className="grid gap-6 md:grid-cols-3 mb-6">
        {/* Cron Status */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Cron Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <span className="text-2xl font-bold">
                {cronStatus?.isSetup ? 'Active' : 'Inactive'}
              </span>
              <Badge variant={cronStatus?.isSetup ? 'default' : 'secondary'}>
                {cronStatus?.isSetup ? (
                  <CheckCircle className="w-3 h-3 mr-1" />
                ) : (
                  <XCircle className="w-3 h-3 mr-1" />
                )}
                {cronStatus?.isSetup ? 'Running' : 'Not Setup'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Next Run */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Next Scheduled Run
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              <div className="text-2xl font-bold">
                {hoursUntil}h {minutesUntil}m
              </div>
              <div className="text-xs text-muted-foreground">
                {nextRun.toLocaleString()}
              </div>
              <Badge variant="outline" className="text-xs">
                Daily at 6:00 PM AEST
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Last Execution */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Last Execution
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingExecution ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : lastExecution ? (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  {lastExecution.status === 'success' ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-500" />
                  )}
                  <span className="font-medium capitalize">{lastExecution.status}</span>
                </div>
                <div className="text-xs text-muted-foreground">
                  {formatDate(lastExecution.created_at)}
                </div>
                {lastExecution.articles_published > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {lastExecution.articles_published} articles
                  </Badge>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No executions yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Setup Alert */}
      {!cronStatus?.isSetup && (
        <Alert className="mb-6 border-yellow-600 bg-yellow-50 dark:bg-yellow-900/20">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertDescription>
            <div className="space-y-2">
              <p className="font-semibold">Cron job is now active!</p>
              <p className="text-sm">
                Your daily automation is scheduled to run at 6:00 PM AEST (8:00 AM UTC) every day.
                The system will automatically collect market data, analyze tokens, and publish articles.
              </p>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Manual Trigger */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PlayCircle className="w-5 h-5" />
            Manual Trigger
          </CardTitle>
          <CardDescription>
            Run the automation pipeline immediately without waiting for the scheduled time
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="bg-muted p-4 rounded-lg">
              <h4 className="font-semibold mb-2">Pipeline Steps:</h4>
              <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
                <li>Collect crypto market data (CoinGecko, DeFiLlama)</li>
                <li>Analyze 100+ tokens for opportunities</li>
                <li>Score and rank using AI analysis</li>
                <li>Generate 2 professional articles</li>
                <li>Publish to website and social media</li>
              </ol>
            </div>
            
            <Button
              onClick={() => runNow.mutate()}
              disabled={isRunning}
              size="lg"
              className="w-full"
            >
              {isRunning ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Running Pipeline...
                </>
              ) : (
                <>
                  <PlayCircle className="w-4 h-4 mr-2" />
                  Run Now
                </>
              )}
            </Button>
            
            {isRunning && (
              <Alert>
                <Loader2 className="h-4 w-4 animate-spin" />
                <AlertDescription>
                  Pipeline is running. This typically takes 3-5 minutes. You can close this page safely.
                </AlertDescription>
              </Alert>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Execution History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Execution History
          </CardTitle>
          <CardDescription>Recent automation runs and their results</CardDescription>
        </CardHeader>
        <CardContent>
          {loadingHistory ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : executionHistory && executionHistory.length > 0 ? (
            <div className="space-y-4">
              {executionHistory.map((execution, index) => (
                <div key={execution.id}>
                  {index > 0 && <Separator />}
                  <div className="py-3 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {execution.status === 'success' ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-500" />
                      )}
                      <div>
                        <div className="font-medium capitalize">{execution.status}</div>
                        <div className="text-sm text-muted-foreground">
                          {formatDate(execution.created_at)}
                        </div>
                        {execution.error_message && (
                          <div className="text-xs text-red-500 mt-1">
                            {execution.error_message}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-right space-y-1">
                      {execution.articles_published > 0 && (
                        <Badge variant="secondary">
                          {execution.articles_published} articles
                        </Badge>
                      )}
                      {execution.execution_time_seconds && (
                        <div className="text-xs text-muted-foreground">
                          {formatDuration(execution.execution_time_seconds)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No execution history yet</p>
              <p className="text-sm">Run the automation manually or wait for the scheduled run</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
